<script lang="ts" setup>
import projectStore from '@/store/ProjectStore.ts'
import appStore from '@/store/AppStore.ts'
import userStore from '@/store/UserStore.ts'
import formStore from '@/store/FormStore.ts'
import {
  BusEvent,
  Category,
  CharacteristicType,
  CustomArticleType,
  HpStep,
  KeyNumType,
  ModalResultType,
  ProductType
} from '@/helper/Types.ts'
import bus from '@/helper/Bus.ts'
import { generateRandomId } from '@/helper/tools.ts'
import { validateComponent, ValidatorType } from '@/helper/ValidateDirective.ts'
import SectionHeadline from '@/components/SectionHeadline.vue'
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import CategoryFilterBar from '@/components/steps/accessories/CategoryFilterBar.vue'
import { Accessory } from '@/services/models/implementations/Accessory.ts'
import CustomArticleListItem from '@/components/steps/accessories/CustomArticleListItem.vue'
import AccessoryListItem from '@/components/steps/accessories/AccessoryListItem.vue'
import { api } from '@/services/ApiService.ts'
import { AccessoryCharacteristic } from '@/services/models/AccessoryCharacteristic.ts'
import FormController from '@/controllers/FormController.ts'
import ModalParams from '@/services/modals/ModalParams.ts'
import { ModalTypes } from '@/helper/ModalTypes.ts'
import { modalService } from '@/services/modals/ModalService.ts'
import { Article } from '@/services/models/Article.ts'
import { MissingAccessory } from '@/services/models/MissingAccessory.ts'

const OTHERS_GROUP = 'others'

const validator = reactive<ValidatorType>({})

const selectedCustomArticles = reactive<KeyNumType>({})
const selectedCategory = ref<Category['label']>('all')
const accessoriesLoaded = ref<boolean>(false)
const enlargeBoxId = ref<string>('')
const suggestedAccessories = ref<Article[]>([])
const accessoryMap = ref<Record<string, Article[]>>({})
const groupExpandedMap = ref<Record<string, boolean>>({})

const emit = defineEmits<{
  (e: 'validStateChange', state: boolean): void
}>()

prefillSelectedCustomArticles()

bus.$onC(HpStep.ACCESSORIES, BusEvent.LANGUAGE_CHANGED, async () => {
  await loadAccessories()
})

onMounted(async () => {
  await loadAccessories()
  FormController.captureInitialData()
})

onUnmounted((): void => {
  bus.$offC(HpStep.ACCESSORIES)
  emit('validStateChange', true)
})

watch(() => suggestedAccessories.value, val => {
  val.forEach(accessory => {
    (formStore.data.selectedAccessories as Record<string, number>)[accessory.id] = accessory.quantity
  })
}, { deep: true })

const accessoryGroups = computed(() => {
  const ag = userStore.accessoryGroups.map(g => g.group)
  if (!ag.includes(OTHERS_GROUP)) {
    ag.push(OTHERS_GROUP)
  }
  return ag
})

const categories = computed((): Category[] => {
  return accessoryGroups.value.map(group => {
    const customArticleQuantity = getCustomArticlesByGroup(group).reduce((acc, customArticle) => acc + customArticle.quantity, 0)
    const accessoryQuantity = accessoryMap.value[group]?.reduce((acc, accessory) => acc + accessory.quantity || 0, 0)

    return {
      label: group,
      count: accessoryQuantity + customArticleQuantity
    }
  })
})

async function loadAccessories() {
  suggestedAccessories.value = await getAccessories()

  for (const accessoryGroup of accessoryGroups.value) {
    loadAccessoriesMap(accessoryGroup)
  }
}

function toggleGroupExpanded(group: string) {
  groupExpandedMap.value[group] = !groupExpandedMap.value[group]
  loadAccessoriesMap(group)
}

function isExpandButtonEnabled(group: string): boolean {
  const groupAccessories = suggestedAccessories.value
    .filter(ac => ac.details?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.ACCESSORY)?.group === group)

  return groupAccessories.some(a => a.quantity === 0)
}

function isGroupEmpty(group: string): boolean {
  const groupAccessories = suggestedAccessories.value
    .filter(ac => ac.details?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.ACCESSORY)?.group === group)

  return groupAccessories.length === 0
}

async function getAccessories(): Promise<Article[]> {
  const hasSelectedAccessories = Object
    .values((projectStore.projectProperties.selectedAccessories.val as KeyNumType) || {})
    .reduce((total, q) => total + q, 0) > 0

  const explicitlyNoAccessoriesSelected = projectStore.projectProperties.explicitlyNoAccessoriesSelected.val

  const accessoryResponse = await api.loadProducts(projectStore.projectId, ProductType.ACCESSORY)
  const accessories = accessoryResponse
    .map(a => Object.assign(new Accessory(), a))
    .map(a => {
      const article = new Article({ id: a.id, details: a })

      if (!hasSelectedAccessories && !explicitlyNoAccessoriesSelected) {
        article.quantity = article.details?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.ACCESSORY).suggestedQuantity ?? 0
        return article
      }

      if (article.details?.inSalePackage) {
        article.quantity = article.details?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.ACCESSORY).inSaleSetQuantity
        return article
      }

      article.quantity = (formStore.data.selectedAccessories as KeyNumType || [])[a.id] || 0 as number

      return article
    })

  accessoriesLoaded.value = true

  return accessories
}

function loadAccessoriesMap(group: string): void {
  accessoryMap.value[group] = []

  for (const accessory of suggestedAccessories.value) {
    const accessoryGroup = accessory.details?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.ACCESSORY)?.group

    if (accessoryGroup !== group) {
      continue
    }

    if (!(group in accessoryMap.value)) {
      accessoryMap.value[group] = []
    }

    if (groupExpandedMap.value[group] || accessory.quantity > 0 || !userStore.enableAccessoryShortList) {
      accessoryMap.value[group].push(accessory)
    }
  }
}

function hasQuantityChanges(group: string): boolean {
  const accessoriesByGroup = getSuggestedAccessoriesByGroup(group)

  return accessoriesByGroup.some(a => {
    return a.quantity !== a.details
      ?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.ACCESSORY)
      .suggestedQuantity
  })
}

function resetQuantities(group: string): void {
  getSuggestedAccessoriesByGroup(group).forEach(a => {
    a.quantity = a.details?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.ACCESSORY).suggestedQuantity || 0
  })

  loadAccessoriesMap(group)
}

function getSuggestedAccessoriesByGroup(group: string): Article[] {
  return suggestedAccessories.value
    .filter(article => article.details?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.ACCESSORY)?.group === group)
}

function getCustomArticlesByGroup(group: string): CustomArticleType[] {
  return (formStore.data.customArticles as CustomArticleType[]).filter(c => c.group === group)
}

function addCustomArticleClicked(group: string): void {
  const id = generateRandomId()

  setTimeout(() => {
    // Delay for the click so the button doesn't immediatly move away
    (formStore.data.customArticles as CustomArticleType[]).push({
      id: id,
      name: '',
      price: undefined,
      quantity: 1,
      group: group
    })

    selectedCustomArticles[id] = 1
  }, 50)
}

function deleteCustomArticle(id: string): void {
  const deleteIndex = (formStore.data.customArticles as CustomArticleType[]).findIndex(prod => prod.id === id)
  setTimeout(() => {
    (formStore.data.customArticles as CustomArticleType[]).splice(deleteIndex, 1)
  }, 50)
}

function prefillSelectedCustomArticles(): void {
  (formStore.data.customArticles as CustomArticleType[]).forEach(product => {
    const id = product.id || ''
    selectedCustomArticles[id] = product.quantity
  })
}

function resetCustomPriceOnAccessoryChange(): void {
  const customPricesProp = projectStore.projectProperties.customPrices
  const projectAccessories = projectStore.projectProperties.selectedAccessories.val as KeyNumType | undefined
  const currentAccessories = formStore.data.selectedAccessories as KeyNumType

  if (customPricesProp.val === undefined || projectAccessories === undefined) {
    return
  }

  Object.keys(projectAccessories).forEach(key => {
    if (!currentAccessories || projectAccessories[key] !== currentAccessories[key]) {
      delete (customPricesProp.val as KeyNumType)[key]
    }
  })
}

function resetCustomPriceOnCustomArticleChange(): void {
  const customPricesProp = projectStore.projectProperties.customPrices
  const projectCustomArticles = projectStore.projectProperties.customArticles.val as CustomArticleType[] | undefined
  const currentCustomArticles = formStore.data.customArticles as CustomArticleType[]

  if (customPricesProp.val === undefined || projectCustomArticles === undefined) {
    return
  }

  projectCustomArticles.forEach(projectCustomArticle => {
    const currentArticle = currentCustomArticles.find(currentCustomArticle => currentCustomArticle.id === projectCustomArticle.id)

    if (currentArticle && (projectCustomArticle.price !== currentArticle.price || projectCustomArticle.quantity !== currentArticle.quantity)) {
      delete (customPricesProp.val as KeyNumType)[projectCustomArticle.id]
    }
  })
}

function detailsClicked(refId: string): void {
  enlargeBoxId.value = enlargeBoxId.value === refId ? '' : refId
}

async function showMissingAccessoriesModal(missingAccessories: Article[]): Promise<boolean> {
  const modalParams = new ModalParams(ModalTypes.ACCESSORY_SUGGESTION, { articles: missingAccessories })
  const modalResult = await modalService.showModal(modalParams)

  return modalResult.resultType === ModalResultType.CONFIRM
}

function getSelectedAccessories(): Article[] {
  const result: Article[] = []

  const formStoreAccessories = Object.keys(formStore.data.selectedAccessories as Record<string, number>)
    .map(id => new Article({
      id: id,
      quantity: formStore.data.selectedAccessories[id],
      details: suggestedAccessories.value.find(sa => sa.id === id)?.details,
    }))

  formStoreAccessories
    .filter(a => a.quantity > 0)
    .forEach(a => result.push(a))

  const projectAccessories = projectStore.getArticlesByCharacteristic(
    CharacteristicType.SYSTEM_CONTROLLER,
    CharacteristicType.INTERNET_GATEWAY,
    CharacteristicType.REMOTE_CONTROL,
    CharacteristicType.CONTROLLER_EXTENSION
  ).filter(a => !result.map(aa => aa.id).includes(a.id))

  projectAccessories.forEach(a => result.push(a))

  return result
}

async function handleMissingAccessories(missingAccessories: Article[]): Promise<boolean> {
  if (missingAccessories.length === 0) {
    return true
  }

  return showMissingAccessoriesModal(missingAccessories)
}

function handleExplicitlyNoAccessoriesSelected(): void {
  const selectedAccessories = formStore.data.selectedAccessories as Record<string, number>
  const hasSelectedAccessories = Object.values(selectedAccessories).some(quantity => quantity > 0)

  formStore.data.explicitlyNoAccessoriesSelected = !hasSelectedAccessories
}

function resolveOrigins(origins: string[]): string[] {
  return [
    ...new Set(getSelectedAccessories()
      .filter(a => origins.includes(a.id))
      .filter(a => a.details !== undefined)
      .map(a => a.details!.name)
    )
  ]
}

async function validate(leaveStep = true): Promise<boolean> {
  if (!validateComponent(validator)) {
    return false
  }

  if (!accessoriesLoaded.value) {
    return true
  }

  if (leaveStep) {
    handleExplicitlyNoAccessoriesSelected()

    let continueToNextStep: boolean = true

    if (!formStore.data.missingAccessoriesConfirmed) {
      const selectedAccessories = getSelectedAccessories().map(a => ({ id: a.id, quantity: a.quantity }))
      const missingAccessories: MissingAccessory[] = (await api.loadMissingAccessories(selectedAccessories))
        .map(missingAccessory => new MissingAccessory({
          ...missingAccessory,
          details: suggestedAccessories.value.find((a: Article) => a.id === missingAccessory.id)?.details,
          origins: resolveOrigins(missingAccessory.origins || []),
        }))
        .filter(missingAccessory => suggestedAccessories.value.map(a => a.id).includes(missingAccessory.id))

      continueToNextStep = await handleMissingAccessories(missingAccessories)
    }

    resetCustomPriceOnAccessoryChange()
    resetCustomPriceOnCustomArticleChange()
    formStore.forceSave = true

    return continueToNextStep
  }

  return true
}

defineExpose({
  validate,
  validator
})
</script>

<template>
  <div class="steps__content" data-hj-whitelist>
    <form
      v-if="Object.keys(accessoryMap).length > 0" id="accessories-form" action="" class="steps__form steps-table"
      @submit.prevent=""
    >
      <CategoryFilterBar
        v-if="categories?.length"
        v-model="selectedCategory"
        :categories="categories"
        :validate="() => validate(false)"
      />

      <!-- AccessoryList -->
      <section v-for="(group, index) in accessoryGroups" :key="index">
        <div v-if="group === selectedCategory || selectedCategory === 'all'" class="accessories__group">
          <div class="steps-table__header">
            <SectionHeadline :translation-key="`step_accessories.group_${group}`" />

            <!-- Reset button -->
            <button
              v-if="accessoryMap[group]?.length"
              :class="{'is--disabled': !hasQuantityChanges(group)}"
              :disabled="!hasQuantityChanges(group)"
              class="btn is--secondary"
              @click="resetQuantities(group)">
              <i class="ic ic-reset" />
              {{ $t('step_accessories.group_preselection_reset') }}
            </button>
          </div>

          <div :data-group="group" class="card">
            <div
              v-if="(accessoryMap[group]?.length + getCustomArticlesByGroup(group).length) > 0"
              class="fTable is--products-table">
              <!-- Table header -->
              <div class="fTable__tr hide-mobile">
                <div class="fTable__th column is--product">
                  <span>{{ $t('step_accessories.table_product') }}</span>
                </div>
                <div class="fTable__th column is--order-number">
                  <span>{{ $t('step_accessories.table_order_number') }}</span>
                </div>
                <div v-if="!userStore.hidePrices" class="fTable__th column is--price">
                  <span>{{ $t('step_accessories.table_single_price') }}
                    <a
                      :title="$t('step_accessories.legal_price_note')" class="bottom-note-anchor"
                      href="#foot-note"
                    >*</a>
                  </span>
                </div>
                <div class="fTable__th column is--details" />
                <div class="fTable__th column is--spinner-controls" />
              </div>

              <AccessoryListItem
                v-for="accessoryArticle in accessoryMap[group]"
                :key="`accessory-${accessoryArticle.id}`"
                :article="accessoryArticle"
                :enlarge-box-id="enlargeBoxId"
                :group="group"
                @show-details="id => detailsClicked(id)"
              />

              <CustomArticleListItem
                v-for="customArticle in getCustomArticlesByGroup(group)"
                :key="`custom-article-${customArticle.id}`"
                :custom-article="customArticle"
                :group="group"
                :validator="validator"
                @delete-custom-article="id => deleteCustomArticle(id)" />
            </div>

            <!-- Empty group message -->
            <div v-else class="accessories__empty-group">
              <div class="h2">
                {{ $t('step_accessories.group_add_title.' + group) }}
              </div>
              <span>
                {{ $t('step_accessories.group_add_description.' + (group === 'services' ? 'services' : 'material')) }}
              </span>
            </div>

            <!-- Custom article add button -->
            <div
              class="accessories__add-item fTable__add-item">
              <button
                class="btn is--tertiary fTable__add-item-btn"
                @click="addCustomArticleClicked(group)">
                <span>{{ $t('global.btn_add') }}</span>
                <i class="ic-plus" />
              </button>
            </div>
          </div>

          <!-- Toggle selected / unselected -->
          <div v-if="userStore.enableAccessoryShortList && !isGroupEmpty(group)" class="accessory-list-expand-toggle">
            <button :disabled="!isExpandButtonEnabled(group)" class="btn is--secondary" @click="toggleGroupExpanded(group)">
              {{ groupExpandedMap[group] ? $t('step_accessories.group_toggle_collapse') : $t('step_accessories.group_toggle_expand') }}
            </button>
          </div>
        </div>
      </section>

      <div id="foot-note" class="foot-note">
        * {{ $t('step_accessories.legal_price_note') }}
      </div>

      <input class="is--invisible" type="submit">
    </form>

    <span v-if="!Object.keys(accessoryMap).length && !appStore.isLoading" class="steps__info">
      {{ $t('global.no_accessories_available_for_selection') }}
    </span>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.card {
  overflow: visible;
}

.accessories {
  &__group {
    margin: rem-calc(48) 0;

    &:last-child {
      margin-bottom: 0;
    }

    .accessory-list-expand-toggle {
      display: flex;
      justify-content: center;
      position: relative;
      top: -1.25rem;

      button {
        background-color: white;
      }

      .btn.is--secondary:disabled {
        background-color: white;
      }
    }

    .btn.is--secondary:disabled {
      opacity: 1;
      background-color: transparent;
      color: lighten($textColor, 60) !important;
      border-color: lighten($borderColor, 0) !important;
    }
  }

  &__group-title {
    margin-bottom: rem-calc(16);
  }

  &__add-item {
    padding: rem-calc(24) rem-calc($cellPaddingLeftMobile);

    @include media('>=desktop') {
      padding: rem-calc(24) rem-calc($cellPaddingLeft);
    }

    .fTable__add-item-btn {
      min-width: auto;
    }
  }

  &__empty-group {
    padding: rem-calc(32) rem-calc(32) 0 rem-calc(32);

    .h2 {
      color: $textColor;
      margin-bottom: rem-calc(16);
    }
  }
}

.fTable {
  &__tr {
    @include themify($themes) {
      border-top-left-radius: rem-calc(themed('cardBorderRadius'));
      border-top-right-radius: rem-calc(themed('cardBorderRadius'));
    }
  }

  &__td {
    span {
      font-size: rem-calc(16);
    }
  }

  &.is--products-table .fTable__th:first-child {
    padding-left: rem-calc(16);
  }

  &.is--products-table .fTable__th:first-child span {
    display: block;
    min-width: rem-calc(120);
    text-align: center;
  }
}
</style>
